/**
 * Link dialog (spec §8.11): predicate picker from SHACL shapes and `rdfs:domain`, target picker
 * with Blocks / Ontology terms / Concepts / IRI / Literal tabs. Writes one `links[linkId]` entry.
 */
import { useT } from "../i18n";
import { useMemo, useState } from "react";
import type { LoroDoc } from "loro-crdt";
import { useBlockSearch, usePredicates, useTermSearch, type PredicateHit } from "../api/hooks";
import { readConcepts } from "../concepts/model";
import { km } from "../lib/iri";
import type { DocHandle } from "../sync/docManager";
import { useLoroSelector } from "../sync/hooks";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { choicesFor, literalError, literalObject, RDF_LANG_STRING } from "./literals";
import { iriTarget, pageTarget, type LinkObject } from "./model";
import type { LinkSubject } from "./subject";


type Tab = "blocks" | "terms" | "concepts" | "iri" | "literal";

const TAB_KEYS: Record<Tab, string> = {
  terms: "linkDialog.tab.terms",
  blocks: "linkDialog.tab.blocks",
  concepts: "linkDialog.tab.concepts",
  iri: "linkDialog.tab.iri",
  literal: "linkDialog.tab.literal",
};

export function LinkDialog({
  subject,
  wsHandle,
  workspaceId,
  userId,
  onClose,
}: {
  subject: LinkSubject;
  wsHandle: DocHandle | null;
  workspaceId: string;
  userId: string;
  onClose(): void;
}) {
  const subjectType = subject.subjectType;
  const predicates = usePredicates(subjectType);
  const [chosen, setChosen] = useState<string | null>(null);
  const [freeIri, setFreeIri] = useState("");
  const t = useT();
  const [rawTab, setTab] = useState<Tab>("terms");
  const [q, setQ] = useState("");
  const [target, setTarget] = useState<LinkObject | null>(null);
  const [note, setNote] = useState("");
  // [M18] the typed Literal tab (spec §8.11 item 5)
  const [literalValue, setLiteralValue] = useState("");
  const [datatype, setDatatype] = useState<string | null>(null);
  const [lang, setLang] = useState("");

  const [narrow, setNarrow] = useState(true);
  const concepts = useLoroSelector(wsHandle?.doc ?? null, (d: LoroDoc) => readConcepts(d, workspaceId), (a, b) => a.length === b.length && a.every((x, i) => x.id === b[i]!.id && x.prefLabel === b[i]!.prefLabel)) ?? [];

  // default to km:mentions (or the first allowed predicate) until the user picks one
  const predicate = chosen ?? (predicates.data?.find((p) => p.iri === km("mentions")) ?? predicates.data?.[0])?.iri ?? "";
  // a new predicate may allow other targets entirely, so the target and the datatype start over
  const setPredicate = (next: string) => {
    setChosen(next);
    setTarget(null);
    setDatatype(null);
  };
  const chosenPredicate: PredicateHit | undefined = predicates.data?.find((p) => p.iri === predicate);
  // §8.11 item 4: the predicate's object class narrows the term tab (km:role → the planning roles)
  const objectType = narrow && predicate !== "__free__" ? (chosenPredicate?.objectClass ?? null) : null;
  const effectivePredicate = predicate === "__free__" ? freeIri.trim() : predicate;
  // a predicate constrained to a datatype takes literals only, one constrained to a class takes IRIs
  const objectDatatype = chosenPredicate?.objectDatatype ?? null;
  const literalOnly = !!objectDatatype;
  const iriOnly = !objectDatatype && !!chosenPredicate?.objectClass;
  const tabs: Tab[] = literalOnly ? ["literal"] : iriOnly ? ["terms", "blocks", "concepts", "iri"] : ["terms", "blocks", "concepts", "iri", "literal"];
  // the chosen tab only holds while the predicate allows it
  const tab = tabs.includes(rawTab) ? rawTab : tabs[0]!;
  // [M24] pages are link targets too (spec §8.25): the tab lists them first
  const blocks = useBlockSearch(workspaceId, q, tab === "blocks", true);
  const terms = useTermSearch(q, tab === "terms", { types: objectType ? [objectType] : [] });
  const datatypes = choicesFor(objectDatatype);
  const effectiveDatatype = datatype && datatypes.some((d) => d.iri === datatype) ? datatype : datatypes[0]!.iri;
  const literalProblem = tab === "literal" ? literalError(literalValue, effectiveDatatype, lang) : null;
  /** a datatype as the reader names it: its catalogue name, or its own local name if we have none */
  const typeName = (iri?: string) => {
    const d = datatypes.find((x) => x.iri === iri);
    return d?.labelKey ? t(d.labelKey) : (d?.label ?? iri?.split(/[#/]/).pop() ?? "");
  };
  const canSave = !!effectivePredicate && !!target && !literalProblem;
  const filteredConcepts = useMemo(
    () => concepts.filter((c) => !q.trim() || c.prefLabel.toLowerCase().includes(q.toLowerCase())),
    [concepts, q],
  );

  const setLiteral = (value: string, dt: string, language: string) => {
    setLiteralValue(value);
    setDatatype(dt);
    setLang(language);
    setTarget(literalError(value, dt, language) ? null : literalObject(value, dt, language));
  };

  const save = () => {
    if (!target || !effectivePredicate) return;
    subject.addLink(effectivePredicate, target, userId, note.trim() || undefined);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(28,27,26,0.36)] p-8" onClick={onClose}>
      <div className="max-h-full w-[46rem] overflow-auto rounded-lg border border-neutral-200 bg-[var(--surface)] p-5 shadow-[var(--shadow-lg)] dark:border-neutral-700" onClick={(e) => e.stopPropagation()} data-testid="link-dialog">
        <h2 className="mb-3 text-base font-semibold">{t("linkDialog.title")}</h2>

        <label className="mb-1 block text-xs font-medium text-neutral-500">{t("linkDialog.predicate")}</label>
        <select className="mb-1 w-full kss-control kss-field bg-transparent px-3 py-[7px] text-sm" value={predicate} onChange={(e) => setPredicate(e.target.value)} data-testid="link-predicate">
          {predicates.data?.map((p) => (
            <option key={p.iri} value={p.iri}>
              {p.label} — {p.iri}
            </option>
          ))}
          <option value="__free__">{t("linkDialog.otherIri")}</option>
        </select>
        {predicate === "__free__" && (
          <div className="mb-2">
            <Input placeholder="https://example.org/vocab#relatesTo" value={freeIri} onChange={(e) => setFreeIri(e.target.value)} data-testid="link-predicate-free" />
            <p className="mt-1 text-xs text-amber-700">{t("linkDialog.unconstrained")}</p>
          </div>
        )}
        {chosenPredicate?.definition && <p className="mb-2 text-xs text-neutral-500">{chosenPredicate.definition}</p>}

        <label className="mb-1 mt-3 block text-xs font-medium text-neutral-500">{t("linkDialog.target")}</label>
        <div className="mb-2 flex gap-1 text-sm">
          {tabs.map((id) => (
            <button key={id} className={"rounded px-2 py-0.5 " + (tab === id ? "bg-accent text-white" : "hover:bg-neutral-200 dark:hover:bg-neutral-800")} onClick={() => { setTab(id); setTarget(null); }} data-testid={`link-tab-${id}`}>
              {t(TAB_KEYS[id])}
            </button>
          ))}
        </div>

        {(tab === "terms" || tab === "blocks" || tab === "concepts") && (
          <Input autoFocus placeholder={t("linkDialog.search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="link-search" />
        )}
        {tab === "terms" && chosenPredicate?.objectClass && (
          <label className="mt-1 flex items-center gap-1 text-xs text-neutral-500" data-testid="link-narrow">
            <input type="checkbox" checked={narrow} onChange={(e) => setNarrow(e.target.checked)} />
            {t("linkDialog.only", { class: chosenPredicate.objectClass.split(/[#/]/).pop() ?? "" })}
          </label>
        )}

        <div className="mt-2 max-h-64 overflow-auto rounded border border-neutral-200 dark:border-neutral-800">
          {tab === "terms" &&
            (terms.data ?? []).map((term) => (
              <Row key={term.iri} selected={target?.value === term.iri} onClick={() => setTarget({ kind: "iri", value: term.iri })} testId="link-option">
                <span className="font-medium">{term.label}</span> <span className="text-xs text-neutral-500">{term.iri}</span>
                {term.deprecated && <span className="ml-1 text-xs text-red-600">{t("linkDialog.deprecated")}</span>}
                {term.definition && <div className="text-xs text-neutral-500">{term.definition.slice(0, 140)}</div>}
              </Row>
            ))}
          {tab === "blocks" &&
            (blocks.data ?? [])
              .filter((b) => b.iri !== subject.iri)
              .map((b) =>
                b.blockType === "page" ? (
                  <Row key={b.iri} selected={target?.value === b.iri} onClick={() => setTarget(pageTarget(b.docId))} testId="link-option">
                    <span className="font-medium">{b.label || t("page.untitled")}</span> <span className="text-xs text-neutral-500">{t("linkDialog.page")}</span>
                  </Row>
                ) : (
                  <Row key={b.iri} selected={target?.value === b.iri} onClick={() => setTarget({ kind: "block", value: b.iri })} testId="link-option">
                    <span className="font-medium">{b.label || t("block.menu.untitledSequence")}</span>{" "}
                    <span className="text-xs text-neutral-500">{b.blockType} · {b.docTitle}</span>
                  </Row>
                ),
              )}
          {tab === "concepts" && (
            <>
              {filteredConcepts.map((c) => (
                <Row key={c.id} selected={target?.value === c.iri} onClick={() => setTarget({ kind: "iri", value: c.iri })} testId="link-option">
                  <span className="font-medium">{c.prefLabel}</span> <span className="text-xs text-neutral-500">{t("linkDialog.workspaceConcept")}</span>
                </Row>
              ))}
              {filteredConcepts.length === 0 && <p className="p-2 text-sm text-neutral-500">{t("linkDialog.noConcepts")}</p>}
            </>
          )}
          {tab === "iri" && (
            <div className="p-2">
              <Input placeholder="http://purl.obolibrary.org/obo/CHEBI_15377" onChange={(e) => setTarget(iriTarget(e.target.value))} data-testid="link-iri" />
            </div>
          )}
          {tab === "literal" && (
            <div className="space-y-2 p-2">
              <div className="flex gap-2">
                <Input
                  className="grow"
                  placeholder={datatypes.find((d) => d.iri === effectiveDatatype)?.placeholder || t("literal.aValue")}
                  value={literalValue}
                  onChange={(e) => setLiteral(e.target.value, effectiveDatatype, lang)}
                  data-testid="link-literal"
                />
                <select
                  className="kss-control kss-field bg-transparent px-3 py-[7px] text-sm"
                  value={effectiveDatatype}
                  onChange={(e) => setLiteral(literalValue, e.target.value, lang)}
                  aria-label={t("linkDialog.datatype")}
                  data-testid="link-literal-datatype"
                >
                  {datatypes.map((d) => (
                    <option key={d.iri || "plain"} value={d.iri}>
                      {d.labelKey ? t(d.labelKey) : d.label}
                    </option>
                  ))}
                </select>
                {effectiveDatatype === RDF_LANG_STRING && (
                  <Input className="w-20" placeholder="en" value={lang} onChange={(e) => setLiteral(literalValue, effectiveDatatype, e.target.value)} data-testid="link-literal-lang" />
                )}
              </div>
              {literalProblem && literalValue.trim() !== "" && (
                <p className="text-xs text-red-700" data-testid="link-literal-error">
                  {t(literalProblem.key, { type: typeName(literalProblem.datatype) })}
                </p>
              )}
              <p className="text-xs text-neutral-500">
                {objectDatatype ? t("linkDialog.takesType", { type: typeName(datatypes[0]!.iri) }) : t("linkDialog.valueNotLink")}
              </p>
            </div>
          )}
        </div>

        <Input className="mt-3" placeholder={t("linkDialog.note")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="link-note" />

        <div className="mt-4 flex items-center gap-2">
          <span className="grow truncate text-xs text-neutral-500">
            {target ? `${subject.iri.slice(0, 28)}… ${effectivePredicate} ${target.value}` : t("linkDialog.chooseTarget")}
          </span>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={!canSave} onClick={save} data-testid="link-save">{t("linkDialog.add")}</Button>
        </div>
      </div>
    </div>
  );
}

function Row({ children, selected, onClick, testId }: { children: React.ReactNode; selected: boolean; onClick(): void; testId?: string }) {
  return (
    <button className={"block w-full border-b border-neutral-100 px-2 py-1 text-left text-sm last:border-0 hover:bg-neutral-100 dark:border-neutral-800 dark:hover:bg-neutral-800 " + (selected ? "bg-blue-50 dark:bg-blue-950" : "")} onClick={onClick} data-testid={testId} data-selected={selected}>
      {children}
    </button>
  );
}
